// @ts-nocheck
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { toast } from 'sonner';

interface SendToJiraDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  ideaId: string;
  hasExistingLinks?: boolean;
  onSuccess?: () => void;
}

export function SendToJiraDialog({ open, onOpenChange, workspaceId, ideaId, hasExistingLinks, onSuccess }: SendToJiraDialogProps) {
  const [projects, setProjects] = useState<{ key: string; name: string }[]>([]);
  const [issueTypes, setIssueTypes] = useState<{ id: string; name: string }[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedIssueType, setSelectedIssueType] = useState<string>('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open && workspaceId) {
      loadProjects();
    }
  }, [open, workspaceId]);

  useEffect(() => {
    if (open && selectedProject) {
      loadIssueTypes(selectedProject);
    } else {
      setIssueTypes([]);
      setSelectedIssueType('');
    }
  }, [open, selectedProject]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const { data, error } = await supabase.functions.invoke('jira-metadata', {
        body: { workspace_id: workspaceId, action: 'projects' },
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data?.error);
      setProjects(data?.projects ?? []);
      if (!selectedProject && data?.projects?.length) {
        setSelectedProject(data.projects[0].key);
      }
    } catch (e: any) {
      toast.error('Failed to load Jira projects', { description: e?.message });
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadIssueTypes = async (projectKey: string) => {
    setLoadingTypes(true);
    try {
      const { data, error } = await supabase.functions.invoke('jira-metadata', {
        body: { workspace_id: workspaceId, action: 'createmeta', project_key: projectKey },
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data?.error);
      const types = data?.issue_types ?? [];
      setIssueTypes(types);
      if (types.length && !selectedIssueType) {
        const preferred = ['Story', 'Task', 'Bug', 'Epic'].find((t) => types.some((it: any) => it.name === t));
        setSelectedIssueType(preferred ? types.find((it: any) => it.name === preferred)?.name ?? types[0].name : types[0].name);
      }
    } catch (e: any) {
      toast.error('Failed to load issue types', { description: e?.message });
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedProject || !selectedIssueType) {
      toast.error('Select project and issue type');
      return;
    }
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('jira-create-issue', {
        body: {
          workspace_id: workspaceId,
          idea_id: ideaId,
          project_key: selectedProject,
          issue_type: selectedIssueType.toLowerCase(),
        },
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data?.error + (data?.details ? ` (${data.details})` : ''));
      toast.success('Created in Jira', { description: data?.jira_key });
      onSuccess?.();
      onOpenChange(false);
    } catch (e: any) {
      toast.error('Failed to create Jira issue', { description: e?.message });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-muted rounded-lg">
              <FileText className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle>Send to Jira</DialogTitle>
              <DialogDescription>Create a Jira issue from this idea.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {hasExistingLinks && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                This idea is already linked to Jira. Creating another issue will add an additional link.
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label>Project</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject} disabled={loadingProjects}>
              <SelectTrigger>
                <SelectValue placeholder={loadingProjects ? 'Loading...' : 'Select project'} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.name} ({p.key})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Issue type</Label>
            <Select value={selectedIssueType} onValueChange={setSelectedIssueType} disabled={loadingTypes || !selectedProject}>
              <SelectTrigger>
                <SelectValue placeholder={loadingTypes ? 'Loading...' : 'Select type'} />
              </SelectTrigger>
              <SelectContent>
                {issueTypes.map((t) => (
                  <SelectItem key={t.id} value={t.name}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating || !selectedProject || !selectedIssueType}>
            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Create in Jira
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
